import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [dssn, setDssn] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpDssn, setSignUpDssn] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState('');
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpStep, setSignUpStep] = useState<'check' | 'create'>('check');
  const navigate = useNavigate();
  const { login } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(dssn, password);
      navigate('/merchant/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDSSN = async () => {
    if (!signUpDssn) {
      setError('DSSN is required');
      return;
    }

    setSignUpLoading(true);
    setError('');

    try {
      console.log('Checking merchant DSSN:', signUpDssn);
      console.log('API URL:', `${API_URL}/merchants/check`);
      
      const response = await fetch(`${API_URL}/merchants/check`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ dssn: signUpDssn })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Response data:', data);

      if (data.exists) {
        if (data.has_password) {
          setError('This merchant already has a password. Please login instead.');
          setShowSignUp(false);
        } else {
          setSignUpStep('create');
        }
      } else {
        setError('No merchant found with this DSSN. Please contact support or complete your merchant KYC process.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to verify DSSN. Please try again.');
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleCreatePassword = async () => {
    if (signUpPassword !== signUpConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signUpPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSignUpLoading(true);
    setError('');

    try {
      console.log('Creating password for merchant DSSN:', signUpDssn);
      
      const response = await fetch(`${API_URL}/merchants/create-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ dssn: signUpDssn, password: signUpPassword })
      });

      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        setShowSignUp(false);
        setSignUpStep('check');
        setSignUpDssn('');
        setSignUpPassword('');
        setSignUpConfirmPassword('');
        setError('');
        setDssn(signUpDssn);
        // Optionally auto-fill password? No, let user enter it
      } else {
        setError(data.error || 'Failed to create password');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to create password. Please try again.');
    } finally {
      setSignUpLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-glass">
        <div className="login-header">
          <div className="login-logo">
            <span className="logo-icon">🏪</span>
            <h1>Digital Liberia</h1>
          </div>
          <p className="login-subtitle">Merchant Portal</p>
        </div>

        {!showSignUp ? (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <input
                type="text"
                placeholder="DSSN (Digital Social Security Number)"
                value={dssn}
                onChange={(e) => setDssn(e.target.value.toUpperCase())}
                required
                className="glass-input"
              />
            </div>

            <div className="input-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="glass-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} className="login-btn">
              {loading ? 'Signing in...' : 'Access Merchant Dashboard'}
            </button>

            <div className="signup-link">
              <button 
                type="button" 
                onClick={() => setShowSignUp(true)}
                className="link-btn"
              >
                First time? Set up your merchant password
              </button>
            </div>
          </form>
        ) : (
          <div className="signup-form">
            {signUpStep === 'check' ? (
              <>
                <div className="info-message">
                  <span>ℹ️</span>
                  <p>Enter your DSSN to verify your merchant status. You should have completed KYC verification with our team first.</p>
                </div>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Enter your DSSN"
                    value={signUpDssn}
                    onChange={(e) => setSignUpDssn(e.target.value.toUpperCase())}
                    className="glass-input"
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                <button 
                  onClick={handleCheckDSSN} 
                  disabled={signUpLoading} 
                  className="login-btn"
                >
                  {signUpLoading ? 'Verifying...' : 'Verify Merchant DSSN'}
                </button>
              </>
            ) : (
              <>
                <div className="success-message">
                  <span>✓</span>
                  <p>Merchant verified! Please create your password to access the portal.</p>
                </div>
                <div className="input-group">
                  <input
                    type="password"
                    placeholder="New Password (min. 8 characters)"
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                    className="glass-input"
                  />
                </div>
                <div className="input-group">
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={signUpConfirmPassword}
                    onChange={(e) => setSignUpConfirmPassword(e.target.value)}
                    className="glass-input"
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                <button 
                  onClick={handleCreatePassword} 
                  disabled={signUpLoading} 
                  className="login-btn"
                >
                  {signUpLoading ? 'Creating...' : 'Create Merchant Password'}
                </button>
              </>
            )}
            
            <div className="signup-link">
              <button 
                type="button" 
                onClick={() => {
                  setShowSignUp(false);
                  setSignUpStep('check');
                  setSignUpDssn('');
                  setSignUpPassword('');
                  setSignUpConfirmPassword('');
                  setError('');
                }}
                className="link-btn"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        )}

        <div className="login-footer">
          <p>Secure merchant portal • Manage your business • Process payments</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
